import { z } from "zod";

// ============================================================================
// Canvas & Runtime Types
// ============================================================================

/**
 * Viewport size presets
 */
export type ViewportSize = "desktop" | "tablet" | "mobile";

/**
 * Position of a frame on the canvas
 */
export interface FramePosition {
  /** Frame ID */
  id: string;
  /** X coordinate */
  x: number;
  /** Y coordinate */
  y: number;
}

/**
 * Saved canvas layout
 */
export interface MagicCommentLayout {
  /** Schema version */
  version: 1;
  /** Current viewport size */
  viewport: ViewportSize;
  /** Frame positions */
  frames: FramePosition[];
  /** Camera position and zoom */
  camera: {
    x: number;
    y: number;
    zoom: number;
  };
}

// ============================================================================
// Collaboration / User Types
// ============================================================================

/**
 * User identity for multiplayer collaboration
 */
export interface UserInfo {
  /** Stable, browser-scoped user id (survives reconnects and remounts) */
  id?: string;
  /** Display name */
  name: string;
  /** User color (hex) */
  color: string;
  /** Optional agent slug identifying the automated author */
  agent?: string;
  /** Optional avatar URL */
  avatarUrl?: string;
}

/**
 * Framework identifier for best-effort component metadata.
 */
export type FrameworkId =
  | "react"
  | "vue"
  | "nuxt"
  | "svelte"
  | "vanilla"
  | "unknown";

/**
 * A reply within an annotation thread
 */
export interface AnnotationReply {
  /** Unique identifier */
  id: string;
  /** Reply text content (supports markdown) */
  text: string;
  /** Author information */
  author: UserInfo;
  /** Creation timestamp */
  createdAt: string;
}

/**
 * Generic thread message for embedded and frame-based comments.
 */
export interface ThreadMessage {
  /** Unique identifier */
  id: string;
  /** Message text content */
  text: string;
  /** Author information */
  author: UserInfo;
  /** Creation timestamp */
  createdAt: string;
  /** Last edit timestamp, set only if the message has been edited */
  editedAt?: string;
  /** Soft-delete markers, set when removed from the dashboard */
  deletedAt?: string;
  deletedBy?: string;
}

/**
 * An annotation pin on a frame (with threaded replies)
 */
export interface Annotation {
  /** Unique identifier */
  id: string;
  /** Frame this annotation belongs to */
  frameId: string;
  /** Relative X position (0-1) */
  x: number;
  /** Relative Y position (0-1) */
  y: number;
  /** Annotation text content (supports markdown) */
  text: string;
  /** Author information */
  author: UserInfo;
  /** Creation timestamp */
  createdAt: string;
  /** Whether this annotation is resolved */
  resolved: boolean;
  /** Threaded replies */
  replies: AnnotationReply[];
}

/**
 * Shared frame target used by the canvas workspace.
 */
export interface FrameTarget {
  kind: "frame";
  frameId: string;
  x: number;
  y: number;
  url?: string;
}

/**
 * DOM anchor information for embedded in-app annotations.
 */
export interface DomAnchor {
  selectorPath: string[];
  tagName: string;
  textQuote?: string;
  textContextBefore?: string;
  textContextAfter?: string;
  classList?: string[];
  attributes?: Record<string, string>;
}

/**
 * Spatial snapshot used to recover an annotation if the DOM shifts.
 */
export interface TargetSnapshot {
  pageX: number;
  pageY: number;
  viewportX: number;
  viewportY: number;
  scrollX: number;
  scrollY: number;
  elementRect: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  viewport: {
    width: number;
    height: number;
  };
}

/**
 * Optional framework/component metadata attached when available in development.
 */
export interface ComponentMeta {
  framework: FrameworkId;
  componentName?: string;
  componentPath?: string;
  sourceFile?: string;
}

/**
 * Embedded DOM target.
 */
export interface DomTarget {
  kind: "dom";
  url: string;
  anchor: DomAnchor;
  snapshot: TargetSnapshot;
  componentMeta?: ComponentMeta;
}

/**
 * Canonical annotation target.
 */
export type AnnotationTarget = FrameTarget | DomTarget;

/**
 * Unified annotation thread shape.
 */
export interface AnnotationThread {
  id: string;
  projectId?: string;
  reviewId?: string;
  pageId?: string;
  status: "open" | "managed" | "resolved";
  createdAt: string;
  updatedAt: string;
  author: UserInfo;
  messages: ThreadMessage[];
  target: AnnotationTarget;
  /** Soft-delete markers, set when the whole thread is removed */
  deletedAt?: string;
  deletedBy?: string;
}

/**
 * Cursor position for a remote user
 */
export interface UserPresence {
  /** User identity */
  user: UserInfo;
  /** Canvas cursor X position */
  cursorX: number;
  /** Canvas cursor Y position */
  cursorY: number;
  /** Currently selected frame ID */
  selectedFrameId: string | null;
}

/**
 * Optional provider used by framework adapters to enrich DOM comments.
 */
export interface MetadataProvider {
  framework: FrameworkId;
  isAvailable(): boolean;
  getComponentMeta(element: HTMLElement): ComponentMeta | null;
}

/**
 * Shared collaboration config for embedded comments.
 */
export interface CollaborationConfig {
  serverUrl: string;
  apiKey?: string;
}

/**
 * Public options for the framework-agnostic embed controller.
 */
export interface MagicCommentOptions {
  projectId: string;
  reviewId?: string;
  pageId?: string;
  mode?: "dev" | "preview";
  collaboration?: CollaborationConfig;
  metadataProvider?: MetadataProvider;
  promptForName?: boolean;
  ui?: {
    enabled?: boolean;
    badge?: boolean;
    toolbar?: boolean;
  };
}

/**
 * Public controller for the embedded runtime.
 */
export interface MagicCommentController {
  mount(): void;
  unmount(): void;
  startPinMode(): void;
  stopPinMode(): void;
  updateOptions(next: Partial<MagicCommentOptions>): void;
}

/**
 * Frame lock state (while a user is dragging)
 */
export interface FrameLock {
  /** Frame ID that is locked */
  frameId: string;
  /** User who holds the lock */
  lockedBy: UserInfo;
  /** Timestamp when lock was acquired */
  lockedAt: string;
}

// ============================================================================
// Zod Schemas for Validation
// ============================================================================

export const UserInfoSchema = z.object({
  id: z.string().optional(),
  name: z.string(),
  color: z.string(),
  agent: z.string().optional(),
  avatarUrl: z.string().optional(),
});

export const ThreadMessageSchema = z.object({
  id: z.string(),
  text: z.string(),
  author: UserInfoSchema,
  createdAt: z.string(),
  editedAt: z.string().optional(),
  deletedAt: z.string().optional(),
  deletedBy: z.string().optional(),
});

export const AnnotationReplySchema = z.object({
  id: z.string(),
  text: z.string(),
  author: UserInfoSchema,
  createdAt: z.string(),
});

export const FrameTargetSchema = z.object({
  kind: z.literal("frame"),
  frameId: z.string(),
  x: z.number(),
  y: z.number(),
  url: z.string().optional(),
});

export const DomAnchorSchema = z.object({
  selectorPath: z.array(z.string()),
  tagName: z.string(),
  textQuote: z.string().optional(),
  textContextBefore: z.string().optional(),
  textContextAfter: z.string().optional(),
  classList: z.array(z.string()).optional(),
  attributes: z.record(z.string(), z.string()).optional(),
});

export const TargetSnapshotSchema = z.object({
  pageX: z.number(),
  pageY: z.number(),
  viewportX: z.number(),
  viewportY: z.number(),
  scrollX: z.number(),
  scrollY: z.number(),
  elementRect: z.object({
    x: z.number(),
    y: z.number(),
    width: z.number(),
    height: z.number(),
  }),
  viewport: z.object({
    width: z.number(),
    height: z.number(),
  }),
});

export const ComponentMetaSchema = z.object({
  framework: z.enum(["react", "vue", "nuxt", "svelte", "vanilla", "unknown"]),
  componentName: z.string().optional(),
  componentPath: z.string().optional(),
  sourceFile: z.string().optional(),
});

export const DomTargetSchema = z.object({
  kind: z.literal("dom"),
  url: z.string(),
  anchor: DomAnchorSchema,
  snapshot: TargetSnapshotSchema,
  componentMeta: ComponentMetaSchema.optional(),
});

export const AnnotationTargetSchema = z.discriminatedUnion("kind", [
  FrameTargetSchema,
  DomTargetSchema,
]);

export const AnnotationThreadSchema = z.object({
  id: z.string(),
  projectId: z.string().optional(),
  reviewId: z.string().optional(),
  pageId: z.string().optional(),
  status: z.enum(["open", "managed", "resolved"]),
  createdAt: z.string(),
  updatedAt: z.string(),
  author: UserInfoSchema,
  messages: z.array(ThreadMessageSchema),
  target: AnnotationTargetSchema,
  deletedAt: z.string().optional(),
  deletedBy: z.string().optional(),
});

export const UserPresenceSchema = z.object({
  user: UserInfoSchema,
  cursorX: z.number(),
  cursorY: z.number(),
  selectedFrameId: z.string().nullable(),
});

export const FrameLockSchema = z.object({
  frameId: z.string(),
  lockedBy: UserInfoSchema,
  lockedAt: z.string(),
});

export const FramePositionSchema = z.object({
  id: z.string(),
  x: z.number(),
  y: z.number(),
});

export const MagicCommentLayoutSchema = z.object({
  version: z.literal(1),
  viewport: z.enum(["desktop", "tablet", "mobile"]),
  frames: z.array(FramePositionSchema),
  camera: z.object({
    x: z.number(),
    y: z.number(),
    zoom: z.number(),
  }),
});
